import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, Modal, useRole, formatDate } from '../components/shared';
import { UNIT_LABEL, everyLabel, todayStr } from '../components/PmLog';
import Photo from '../components/Photo';

const addDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d + n).toLocaleDateString('en-CA'); };
const oneYear = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y + 1, m - 1, d - 1).toLocaleDateString('en-CA'); };
const money = v => (v || v === 0) ? `${Number(v).toLocaleString('en')} EGP` : '—';
export const CONTRACT_STATUS = { active: ['ساري', 'badge-green'], draft: ['مسودة', 'badge-gray'], ended: ['منتهي', 'badge-gray'] };

// Shared form for new contract / edit / duplicate
export function ContractForm({ title, initial = {}, clients, mode, devices, onSave, onClose }) {
  const start0 = initial.start_date || todayStr();
  const [f, setF] = useState({
    client_id: '', new_client_name: '', title: '', every_value: 1, every_unit: 'month', value: '', assignee: '', notes: '', status: 'active',
    ...initial, start_date: start0, end_date: initial.end_date || oneYear(start0),
  });
  const [newCenter, setNewCenter] = useState(mode === 'new');
  const [serials, setSerials] = useState({});
  const [skip, setSkip] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const setStart = e => { const s = e.target.value; setF(x => ({ ...x, start_date: s, end_date: x.end_date && x.end_date !== oneYear(x.start_date) ? x.end_date : oneYear(s) })); };

  async function submit() {
    setBusy(true);
    try {
      const body = { ...f, value: f.value === '' ? null : Number(f.value), every_value: Number(f.every_value) || 1 };
      if (newCenter) { delete body.client_id; } else { delete body.new_client_name; }
      if (mode === 'new') { body.serials = serials; body.skip = [...skip]; }
      await onSave(body);
    } catch (e) { toast(e.message, 'error'); }
    finally { setBusy(false); }
  }

  const showCenter = mode !== 'renew' && mode !== 'edit-keep-center';
  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>إلغاء</button>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={submit}>{busy ? '...' : 'حفظ'}</button>
      </>
    }>
      <div dir="rtl">
        {showCenter && (
          <div className="field">
            <label className="flex items-center gap-8">المركز / الفرع *
              {mode !== 'edit' && <button type="button" className="btn btn-ghost btn-sm" style={{ marginInlineStart: 'auto' }} onClick={() => setNewCenter(v => !v)}>
                {newCenter ? 'اختار مركز موجود' : '+ مركز جديد'}</button>}
            </label>
            {newCenter
              ? <input className="input" placeholder="اسم المركز / الفرع" value={f.new_client_name} onChange={set('new_client_name')} autoFocus />
              : <select className="select" value={f.client_id} onChange={set('client_id')}>
                  <option value="">— اختار —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>}
          </div>
        )}
        <div className="field"><label>اسم العقد (اختياري)</label><input className="input" value={f.title || ''} onChange={set('title')} placeholder="عقد صيانة دورية 2026/2027" /></div>
        <div className="grid-2">
          <div className="field"><label>من</label><input className="input" type="date" value={f.start_date} onChange={setStart} /></div>
          <div className="field"><label>إلى</label><input className="input" type="date" min={f.start_date} value={f.end_date} onChange={set('end_date')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>زيارة كل</label>
            <div className="flex gap-8">
              <input className="input" type="number" min="1" style={{ width: 70 }} value={f.every_value} onChange={set('every_value')} />
              <select className="select" value={f.every_unit} onChange={set('every_unit')}>
                {Object.entries(UNIT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div></div>
          <div className="field"><label>قيمة العقد (EGP)</label><input className="input" type="number" value={f.value ?? ''} onChange={set('value')} /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>المسؤول</label><input className="input" value={f.assignee || ''} onChange={set('assignee')} /></div>
          <div className="field"><label>الحالة</label>
            <select className="select" value={f.status} onChange={set('status')}>
              {Object.entries(CONTRACT_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
        </div>
        <div className="field"><label>ملاحظات</label><textarea className="input" rows={2} value={f.notes || ''} onChange={set('notes')} /></div>

        {mode === 'new' && devices && devices.length > 0 && (
          <div className="field">
            <label>الأجهزة اللي هتتنسخ (اكتب سيريال كل جهاز في المركز الجديد، أو سيبه فاضي وكمّله بعدين)</label>
            {devices.map(d => (
              <div key={d.id} className="flex items-center gap-8" style={{ padding: '4px 0', opacity: skip.has(d.id) ? 0.4 : 1 }}>
                <input type="checkbox" checked={!skip.has(d.id)} onChange={() => setSkip(s => { const n = new Set(s); n.has(d.id) ? n.delete(d.id) : n.add(d.id); return n; })} />
                <span className="text-sm" style={{ flex: 1 }}>{d.name} <span className="text-xs text-dim">({d.tasks.length} مهمة)</span></span>
                <input className="input" dir="ltr" style={{ width: 150, padding: '4px 8px' }} placeholder="Serial" disabled={skip.has(d.id)}
                  value={serials[d.id] || ''} onChange={e => setSerials(s => ({ ...s, [d.id]: e.target.value }))} />
              </div>
            ))}
          </div>
        )}
        {mode === 'renew' && <div className="text-xs text-muted">هيتنسخ نفس المركز بكل أجهزته ومهامها لمدة جديدة. الزيارات هتتعمل من جديد.</div>}
      </div>
    </Modal>
  );
}

export default function Maintenance() {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('active');
  const toast = useToast();
  const isAdmin = useRole() === 'admin';
  const navigate = useNavigate();

  const load = useCallback(() => api.pmContracts().then(setContracts).catch(e => toast(e.message, 'error')), [toast]);
  useEffect(() => { load(); api.getClients().then(setClients).catch(() => {}); }, [load]);

  const t = todayStr(), wk = addDays(t, 7);
  const active = contracts.filter(c => c.status === 'active');
  const shown = contracts.filter(c => {
    if (filter === 'active' && c.status !== 'active') return false;
    if (filter === 'overdue' && !c.visits_overdue) return false;
    if (filter === 'ended' && c.status === 'active') return false;
    if (q && ![c.client_name, c.title, c.assignee, c.notes].some(v => v && v.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  async function create(body) {
    const r = await api.pmCreateContract(body);
    toast('اتعمل العقد'); setForm(false); navigate(`/maintenance/${r.id}`);
  }

  return (
    <div className="page" dir="rtl">
      <div className="page-header">
        <h1 className="page-title" dir="ltr">MAINTENANCE <span>_</span></h1>
        <div className="flex gap-8">
          <button className="btn btn-ghost" onClick={() => navigate('/maintenance/labels')}>🏷 طباعة QR</button>
          {isAdmin && <button className="btn btn-primary" onClick={() => setForm(true)}>+ عقد صيانة</button>}
        </div>
      </div>

      <div className="stats-grid mb-20">
        <div className="stat-card"><div className="stat-label">عقود سارية</div><div className="stat-value">{active.length}</div></div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => setFilter('overdue')}>
          <div className="stat-label">زيارات متأخرة</div><div className={`stat-value ${active.some(c => c.visits_overdue) ? 'text-red' : ''}`}>{active.reduce((s, c) => s + c.visits_overdue, 0)}</div></div>
        <div className="stat-card"><div className="stat-label">زيارات الأسبوع ده</div><div className="stat-value">{active.filter(c => c.next_visit && c.next_visit >= t && c.next_visit <= wk).length}</div></div>
        <div className="stat-card"><div className="stat-label">قيمة العقود السارية</div><div className="stat-value" style={{ fontSize: 20 }}>{money(active.reduce((s, c) => s + (c.value || 0), 0))}</div></div>
      </div>

      <div className="flex gap-8 mb-16" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 260 }} placeholder="🔍 مركز، مسؤول..." value={q} onChange={e => setQ(e.target.value)} />
        {[['active', 'سارية'], ['overdue', 'فيها تأخير'], ['ended', 'منتهية / مسودة'], ['all', 'الكل']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${filter === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>

      {shown.length === 0 && <div className="card"><div className="empty">{contracts.length ? 'مفيش نتايج' : 'مفيش عقود لسه — اضغط "+ عقد صيانة"'}</div></div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {shown.map(c => {
          const [sl, scls] = CONTRACT_STATUS[c.status] || [c.status, 'badge-gray'];
          const pct = c.visits_total ? Math.round(c.visits_done / c.visits_total * 100) : 0;
          return (
            <div key={c.id} className="card" onClick={() => navigate(`/maintenance/${c.id}`)} style={{ padding: 16, cursor: 'pointer', borderTop: `3px solid ${c.visits_overdue ? 'var(--red)' : 'var(--primary)'}` }}>
              <div className="flex items-center gap-8" style={{ justifyContent: 'space-between' }}>
                <div className="flex items-center gap-8">
                  <Photo kind="client" id={c.client_id} version={c.client_photo_at} size={44} icon="🏥" />
                  <div className="fw-600" style={{ fontSize: 16 }}>{c.client_name}</div>
                </div>
                <span className={`badge ${scls}`}>{sl}</span>
              </div>
              {c.title && <div className="text-sm text-muted">{c.title}</div>}
              <div className="text-xs text-dim mt-4">{formatDate(c.start_date)} ← {formatDate(c.end_date)} · {everyLabel(c.every_value, c.every_unit)}</div>
              <div className="flex gap-12 mt-8 text-sm" style={{ flexWrap: 'wrap' }}>
                <span>📟 {c.device_count} جهاز</span>
                <span>📅 {c.visits_done}/{c.visits_total} زيارة</span>
                {c.value ? <span>💰 {money(c.value)}</span> : null}
                {c.file_name && <span>📎 العقد</span>}
              </div>
              <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, marginTop: 10, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--green)' }} />
              </div>
              <div className="text-xs mt-8">
                {c.visits_overdue ? <span className="text-red fw-600">⚠ {c.visits_overdue} زيارة متأخرة · </span> : null}
                <span className="text-muted">الزيارة الجاية: {c.next_visit ? formatDate(c.next_visit) : '—'}{c.assignee ? ` · 👤 ${c.assignee}` : ''}</span>
              </div>
            </div>
          );
        })}
      </div>

      {form && <ContractForm title="عقد صيانة دورية جديد" clients={clients} onSave={create} onClose={() => setForm(false)} />}
    </div>
  );
}
